import * as React from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./dialog";
import { Input } from "./input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./select";
import { Button } from "./button";
import { Label, Text, Caption } from "./typography";
import { RadioGroup, RadioGroupItem } from "./radio-group";
import { API_BASE_URL } from '../../lib/api';

/**
 * AddContributionDialog - Dialog for adding a new contribution or withdrawal
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {Function} props.onSuccess - Callback when contribution is created successfully
 * @param {string} props.defaultType - Default type ("deposit" or "withdrawal")
 */
export function AddContributionDialog({
  open,
  onOpenChange,
  onSuccess,
  defaultType = "deposit",
}) {
  const [formData, setFormData] = React.useState({
    type: defaultType,
    amount: "",
    currency: "EUR",
    description: "",
  });

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const CURRENCIES = [
    { value: "EUR", label: "EUR - Euro" },
    { value: "USD", label: "USD - US Dollar" },
  ];

  const isWithdrawal = formData.type === "withdrawal";

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      setFormData({
        type: defaultType,
        amount: "",
        currency: "EUR",
        description: "",
      });
      setError(null);
    }
  }, [open, defaultType]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      return "Amount must be a positive number";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        type: formData.type,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        description: formData.description || "",
      };

      const response = await axios.post(
        `${API_BASE_URL}/contributions`,
        payload,
        { withCredentials: true }
      );

      if (onSuccess) {
        onSuccess(response.data);
      }
      onOpenChange(false);
    } catch (err) {
      let errorMsg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.message;

      if (typeof errorMsg === "object" && errorMsg !== null) {
        errorMsg = errorMsg.message || JSON.stringify(errorMsg);
      }

      console.error("Failed to create contribution:", err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="pb-2">
          <DialogTitle>
            {isWithdrawal ? "Record Withdrawal" : "Record Contribution"}
          </DialogTitle>
          <DialogDescription>
            {isWithdrawal
              ? "Record money withdrawn from your portfolio"
              : "Record new money added to your portfolio"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Error Message */}
          {error && (
            <div className="rounded-md bg-destructive/10 border border-destructive p-3">
              <Text variant="error" size="sm">
                {error}
              </Text>
            </div>
          )}

          {/* Transaction Type Selection */}
          <fieldset className="space-y-3 rounded-lg border border-border/50 p-4">
            <legend className="px-2 text-sm font-medium text-muted-foreground">
              Transaction Type
            </legend>
            <RadioGroup
              value={formData.type}
              onValueChange={(value) => handleSelectChange("type", value)}
              className="flex flex-col space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="deposit" id="deposit" />
                <Label htmlFor="deposit" className="text-sm cursor-pointer">
                  Deposit (money added)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="withdrawal" id="withdrawal" />
                <Label htmlFor="withdrawal" className="text-sm cursor-pointer">
                  Withdrawal (money removed)
                </Label>
              </div>
            </RadioGroup>
            <Caption className="block text-muted-foreground">
              {isWithdrawal
                ? "Withdrawals reduce your invested capital."
                : "Deposits increase your invested capital."}
            </Caption>
          </fieldset>

          {/* Amount and Currency */}
          <fieldset className="space-y-3 rounded-lg border border-border/50 p-4">
            <legend className="px-2 text-sm font-medium text-muted-foreground">
              Amount
            </legend>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">Amount *</Label>
                <Input
                  name="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="e.g., 1000.00"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">Currency *</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => handleSelectChange("currency", value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-foreground text-sm">Description</Label>
              <Input
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="e.g., Monthly investment, Emergency fund withdrawal..."
              />
            </div>
          </fieldset>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : isWithdrawal ? (
                "Record Withdrawal"
              ) : (
                "Record Deposit"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AddContributionDialog;
